// IE Hub - Onboarding Service
// Handles progressive customer data updates during onboarding flow

import { httpDelete, httpGet, httpPost, httpPut } from "../http";
import { femaCapture } from "../../fema/capture";
import { onboardingEndpoints } from "../endpoints";
import {
  coerceConsentToHuman,
  coerceConsentToLegacy,
} from "../../lib/utils/consent";
import type {
  ServiceResponse,
  VulnerabilityCircumstancePayload,
  VulnerabilityCircumstanceResponse,
  CustomerFieldStageRecord,
  CustomerFieldStageResponse,
  CustomerDebtRecord,
  CustomerSharingResponse,
  FreePostDataResponse,
  CustomerSharingUpsertRequest,
  FreePostSharingPayload,
} from "../types";
import { DebtsService } from "./debtsService";
import { SharingOrchestrator } from "./sharingOrchestrator";
import { format, isValid } from "date-fns";

// Income onboarding completion threshold - stages >= this indicate completed income budget onboarding
const INCOME_ONBOARDING_COMPLETED_THRESHOLD = 104;
const ONBOARDING_REQUEST_TIMEOUT_MS = 120_000;

// TypeScript Interfaces
export type VerificationRequiredResponse = {
  verifyCustomer: "Y" | "N";
  noOfAttempts: number;
};

// Creditor Open Banking invitation payload
export type OpenBankInvitationRecord = {
  id: number;
  accountNumber?: string;
  userId?: number;
  sandboxCustomerId?: number;
  creditorCustomerId?: number;
  status?: string;
  customerStatusDate?: number | string;
  schemaName?: string;
  lenderName?: string;
  createdBy?: number;
  creationDate?: number | string;
  lastUpdatedDate?: number | string;
  lastUpdateDate?: number | string;
  lastUpdatedBy?: number;
  // Legacy naming seen in payloads/screenshots
  partnersStatus?: string;
  partnersStatusDate?: number | string;
  // Legacy payloads often use this plural key
  partnersEmailAddress?: string;
  // Some environments may use alternate naming
  partnerEmailAddress?: string;
  // Allow extra properties without losing type-safety
  [key: string]: unknown;
};
interface CustomerRecord {
  id: number;
  customerId: number;
  userId: number;
  emailAddress?: string;
  creationDate?: string;
  lastUpdateDate?: string;
  [key: string]: any;
}

interface CustomerSummaryRecord {
  id: number;
  customerId: number;
  userId: number;
  emailAddress?: string;
  lastUpdateDate?: string;
  [key: string]: unknown;
}

interface CustomerListResponseEnvelope {
  data?: unknown;
  myData?: unknown;
  message?: string;
  status?: number;
}

interface CustomerUpdatePayload {
  id?: number;
  customerId?: number;
  userId: number;
  emailAddress?: string;
  creationDate?: string;
  lastUpdateDate?: string;
  createdBy?: number;
  lastUpdatedBy?: number;
  [key: string]: any;
}

interface GetCustomerResponse {
  status: number;
  data: any;
  message?: string;
}

// Complete field mapping from schema to API format
const SCHEMA_FIELD_MAPPING: Record<string, Record<string, string>> = {
  // Step 0: 2-refered-method.json (1 field)
  "onboarding-step-0": {
    referedFrom: "heardAboutUs",
  },

  // Step 1: 3-refered-from-company.json (5 fields)
  "onboarding-step-1": {
    // Note: referedFromCompany, productName, otherCompanyName, and notReferredByCompany
    // are all handled by special logic to determine field5TxtFld value
    // See special handling section in transformToApiFormat method
  },

  // Step 2: 4-reason-iehub.json (1 field)
  "onboarding-step-2": {
    reasonIEHub: "textField4", // Reason for using IE Hub
  },

  // Step 3: 5-personal-details.json (3 fields)
  "onboarding-step-3": {
    title: "customerTitle", // Mr/Mrs/Ms etc
    firstName: "debtor1FirstName", // First Name
    surname: "debtor1LastName", // Last name/surname
  },

  // Step 4: 6-date-of-birth.json (1 field)
  "onboarding-step-4": {
    dateOfBirth: "debtor1DateOfBirthStr", // Date of birth
  },

  // Step 5: 7-contact-details.json (2 fields)
  "onboarding-step-5": {
    phoneNumber: "mobileNumber", // Mobile number
    emailId: "emailAddress", // Email address
    landlineNumber: "landlineNumber", // Landline number (treat like phone - empty string clears)
  },

  // Step 6: 8-address-details.json (address fields)
  // Backend supports: buildingNumber, buildingName, streetName, postTown, postCode, country, state
  // Note: 'city' is NOT supported by backend CustomerModel - use postTown instead
  "onboarding-step-6": {
    postcode: "postCode", // Post code
    postCode: "postCode", // Post code (alternate key)
    buildingNumber: "buildingNumber", // Building number
    streetName: "streetName", // Street name (from Google)
    postTown: "postTown", // Post town (from Google) - also used for city
    state: "state", // State/region (from Google)
    country: "country", // Country (from Google)
    buildingName: "buildingName", // Building name (from Google)
  },

  // Step 7: 9-vulnerability-circumstances.json (1 field)
  "onboarding-step-7": {
    // 'hasVulnerabilityCircumstances': 'field4TxtFld' // Y/N flag
  },

  // Step 8: 10-yes-vulnerability.json (1 field)
  "onboarding-step-8": {
    vulnerabilityCircumstancesAudience: "field3TxtFld", // Who applies to
  },

  // Step 9: 11-yes-vulnerability.json (1 field)
  "onboarding-step-9": {
    // 'vulnerabilityCircumstancesDetails': 'field5Txtfld' // Circumstances array
  },

  // Step 10: 12-yes-vulnerability.json (1 field)
  "onboarding-step-10": {
    healthDataSharingConsent: "field4TxtFld", // Consent Y/N
  },
};

// Debt field mapping from schema to Debts API format
// Note: This mapping is kept for reference but debt transformation is now done
// manually in transformToDebtFormat to handle complex scenarios correctly
const _DEBT_FIELD_MAPPING: Record<string, Record<string, string>> = {
  // Step 1: 3-refered-from-company.json (debt-related fields)
  "onboarding-step-1": {
    referedFromCompany: "creditor", // Product name -> creditor (lender name)
    otherCompanyName: "creditor", // Other company name -> creditor (lender name)
    productName: "creditor", // Product name -> creditor (lender name)
    accountType: "accTypeCode", // Account type -> accTypeCode
    referenceId: "accountNumber", // Reference ID -> accountNumber
  },
};
// Reference to keep the mapping from being flagged as unused
void _DEBT_FIELD_MAPPING;

// Vulnerability field mapping from schema to Vulnerability API format
const VULNERABILITY_FIELD_MAPPING: Record<string, Record<string, string>> = {
  // Step 8: 10-yes-vulnerability.json
  "onboarding-step-8": {
    vulnerabilityCircumstancesAudience: "field3TxtFld", // Who applies to (MySelf/PartyInMyBudget/Both/None)
  },

  // Step 10: 12-yes-vulnerability.json
  "onboarding-step-10": {
    healthDataSharingConsent: "field4TxtFld", // Consent Y/N
  },
};

/**
 * Onboarding Service Class
 * Handles progressive customer data updates during onboarding
 */
export class OnboardingService {
  // Cache for customer record to avoid repeated GET calls
  private static customerCache: Map<number, Record<string, any>> = new Map();
  private static vulnerabilityCache: Map<
    number,
    VulnerabilityCircumstancePayload
  > = new Map();
  private static fieldStageCache: Map<number, CustomerFieldStageRecord> =
    new Map();

  /**
   * Clear field5TxtFld from cached customer data
   */
  static clearCachedCompanyField(userId: number): void {
    const cached = this.customerCache.get(userId);
    console.log("🧹 clearCachedCompanyField called:", {
      userId,
      hasCached: !!cached,
      currentField5: cached?.field5TxtFld,
    });
    if (cached) {
      cached.field5TxtFld = "";
      this.customerCache.set(userId, cached);
      console.log(
        "✅ field5TxtFld cleared from cache, now:",
        cached.field5TxtFld
      );
    }
  }

  /**
   * Ensure there is a queued referral-based share entry when the customer has
   * selected "Referred by a company" in the "How did you hear about IE Hub?"
   * question, but we have not yet created a referral-managed debt.
   *
   * Rules:
   * - Only runs when step-0 referedFrom === "Referred by a company"
   * - Uses field5TxtFld (company / product name) as the lenderName
   * - Skips when field5TxtFld is empty or "I have not been referred by a company"
   * - Skips when there is already a share with source === "referred" and shareNow === "N"
   */
  private static async ensureReferralPlaceholderShareForHeardAboutUs(
    customerId: number,
    allFormData: Record<string, any>,
    updatePayload: Record<string, any>
  ): Promise<void> {
    // Do not create placeholder shares when user selected manual company input
    try {
      const step1 = allFormData["onboarding-step-1"] as
        | Record<string, unknown>
        | undefined;
      const isOtherCompany =
        typeof step1?.["referedFromCompany"] === "string" &&
        (step1?.["referedFromCompany"] as string) === "OTHER_COMPANY";
      if (isOtherCompany) {
        return;
      }
    } catch {
      // non-blocking guard
    }
    if (!customerId || !Number.isFinite(customerId)) return;

    // Check step-0 "How did you hear about IE Hub?"
    const step0 = allFormData["onboarding-step-0"] as
      | Record<string, unknown>
      | undefined;
    const referMethodRaw =
      typeof step0?.referedFrom === "string"
        ? (step0.referedFrom as string).trim()
        : "";
    if (referMethodRaw !== "Referred by a company") {
      return;
    }

    // Derive a sensible lenderName from field5TxtFld
    const field5Raw =
      typeof (updatePayload as any)?.field5TxtFld === "string"
        ? ((updatePayload as any).field5TxtFld as string).trim()
        : "";
    if (!field5Raw || field5Raw === "I have not been referred by a company") {
      // No meaningful referral company to surface yet
      return;
    }

    // Special-case: Do NOT create a placeholder referral share for "Stop Loan Sharks" (SLS)
    try {
      const normalized = field5Raw.trim().toLowerCase();
      if (
        normalized === "stop loan sharks" ||
        normalized === "sls" ||
        normalized === "Stop Loan Sharks"
      ) {
        return;
      }
    } catch {
      // non-blocking
    }

    try {
      const existingResp = await DebtsService.getCustomerSharing(customerId, {
        skipErrorCapture: true,
      });
      const existingList: CustomerSharingResponse[] = Array.isArray(
        existingResp?.data
      )
        ? (existingResp.data as CustomerSharingResponse[])
        : [];

      // Check if ANY referral share already exists (shared or not).
      // We only need to create a placeholder share ONCE per customer.
      // Previously this only checked for "queued" (never-shared) referral shares,
      // which caused duplicate shares on every profile update after the first share.
      const hasAnyReferralShare = existingList.some((row) => {
        if (!row) return false;
        const src =
          typeof row.source === "string" ? row.source.toLowerCase() : "";
        const isProfileManaged =
          src === "customer_profile" || src === "referred";
        const isDeleted =
          (row as any).isDelete === true || row.isDeleted === "Y";

        // If any non-deleted profile-managed share exists, we don't need to create another
        return isProfileManaged && !isDeleted;
      });
      if (hasAnyReferralShare) {
        // A referral share already exists; nothing to do
        return;
      }

      const payload: CustomerSharingUpsertRequest = {
        customerId,
        lenderName: field5Raw,
        accountNumber: null,
        schemaName: null,
        subDomain: null,
        source: "customer_profile",
        shareNow: "N",
        isSharing: "Y",
      };

      await DebtsService.createCustomerSharing(payload, {
        skipErrorCapture: true,
      });
    } catch (err) {
      console.warn(
        "⚠️ ensureReferralPlaceholderShareForHeardAboutUs: failed to create placeholder share:",
        err
      );
    }
  }

  private static isCustomerSummaryRecord(
    record: unknown
  ): record is CustomerSummaryRecord {
    if (typeof record !== "object" || record === null) {
      return false;
    }

    const candidate = record as Record<string, unknown>;
    const id = candidate.id;
    const customerId = candidate.customerId;
    const userId = candidate.userId;

    return (
      typeof id === "number" &&
      typeof customerId === "number" &&
      typeof userId === "number"
    );
  }

  private static normalizeCustomerListPayload(
    raw: unknown
  ): CustomerSummaryRecord[] {
    if (Array.isArray(raw)) {
      return raw.filter((item) => this.isCustomerSummaryRecord(item));
    }

    if (typeof raw === "object" && raw !== null) {
      const envelope = raw as CustomerListResponseEnvelope &
        Record<string, unknown>;
      const aggregated: CustomerSummaryRecord[] = [];

      const dataCandidate = envelope.data;
      if (Array.isArray(dataCandidate)) {
        aggregated.push(
          ...dataCandidate.filter((item) => this.isCustomerSummaryRecord(item))
        );
      } else if (this.isCustomerSummaryRecord(dataCandidate)) {
        aggregated.push(dataCandidate);
      }

      const myDataCandidate = envelope.myData;
      if (Array.isArray(myDataCandidate)) {
        aggregated.push(
          ...myDataCandidate.filter((item) =>
            this.isCustomerSummaryRecord(item)
          )
        );
      } else if (this.isCustomerSummaryRecord(myDataCandidate)) {
        aggregated.push(myDataCandidate);
      }

      if (aggregated.length > 0) {
        return aggregated;
      }

      if (this.isCustomerSummaryRecord(envelope)) {
        return [envelope];
      }
    }

    return [];
  }

  /**
   * Get existing customer record by userId
   */
  static async getCustomerByUserId(
    userId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<Record<string, any> | null>> {
    try {
      // Check cache first
      const cached = this.customerCache.get(userId);
      if (cached) {
        return {
          success: true,
          data: cached,
          message: "Customer record found in cache",
          timestamp: new Date().toISOString(),
        };
      }

      // Primary attempt: "/customers/{userId}"
      try {
        const response = (await httpGet(
          onboardingEndpoints.getCustomerByUserId(userId),
          {
            timeout: 5000,
            headers: {
              "Content-Type": "application/json",
            },
          }
        )) as GetCustomerResponse;

        const raw = response.data;
        const hasData = Array.isArray(raw) ? raw.length > 0 : Boolean(raw);
        if (response.status === 200 && hasData) {
          const customerData = Array.isArray(raw)
            ? raw[raw.length - 1]
            : (raw as Record<string, any>);
          this.customerCache.set(userId, customerData as Record<string, any>);
          localStorage.setItem("customerData", JSON.stringify(customerData));
          return {
            success: true,
            data: customerData as Record<string, any>,
            message: "Customer found",
            timestamp: new Date().toISOString(),
          };
        }
      } catch (primaryError: any) {
        // Continue to fallback below
        if (
          primaryError?.response?.status !== 404 &&
          !options.skipErrorCapture
        ) {
          femaCapture("onboarding_primary_get_customer_error", primaryError, {
            category: "api",
            severity: "warning",
            context: { userId, endpoint: "customers/{userId}" },
            skipSanitization: false,
            user: {},
          });
        }
      }

      // Fallback attempt: "/ie_free_post_customers/{id}" (Swagger: fetch by customerId)
      // Only attempt fallback when primary returns 404 (not for other errors or empty object responses)
      try {
        const fetched = await this.fetchCustomers(
          { userId },
          { skipErrorCapture: true }
        );
        if (fetched.length > 0) {
          const candidate = fetched[fetched.length - 1];
          this.customerCache.set(userId, candidate as Record<string, any>);
          try {
            localStorage.setItem("customerData", JSON.stringify(candidate));
          } catch {}
          return {
            success: true,
            data: candidate as Record<string, any>,
            message: "Customer found via fallback list",
            timestamp: new Date().toISOString(),
          };
        }
      } catch (fallbackError: any) {
        if (!options.skipErrorCapture) {
          femaCapture("onboarding_fallback_get_customer_error", fallbackError, {
            category: "api",
            severity: "warning",
            context: { userId, endpoint: "ie_free_post_customers/{id}" },
            skipSanitization: false,
            user: {},
          });
        }
      }

      // Customer not found in either endpoint
      try {
        localStorage.removeItem("customerData");
      } catch {}
      return {
        success: false,
        data: null,
        message: "Customer record not found",
        timestamp: new Date().toISOString(),
      };
    } catch (error: any) {
      if (!options.skipErrorCapture) {
        femaCapture("onboarding_get_customer_error", error, {
          category: "api",
          severity: "warning",
          context: { userId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async fetchCustomers(
    params: { userId?: number } = {},
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<CustomerSummaryRecord[]> {
    try {
      // If userId is provided, use the path parameter endpoint instead of query params.
      // IMPORTANT: We intentionally do NOT fall back to the list endpoint when userId is
      // present, because `/iehub-customer/api/v1/customers` (no id) is not supported in
      // some environments and can return 500. In that case we simply return an empty
      // result and let callers treat it as "not found".
      if (typeof params.userId === "number") {
        try {
          const response = (await httpGet(
            onboardingEndpoints.getCustomerByUserId(params.userId),
            {
              timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
              headers: {
                "Content-Type": "application/json",
              },
            }
          )) as GetCustomerResponse;

          const raw = response.data;
          const hasData = Array.isArray(raw) ? raw.length > 0 : Boolean(raw);
          if (response.status === 200 && hasData) {
            const customerData = Array.isArray(raw)
              ? raw[raw.length - 1]
              : (raw as Record<string, any>);
            const records = this.normalizeCustomerListPayload([customerData]);

            // Update cache and localStorage (same as original behavior)
            records.forEach((record) => {
              if (typeof record.userId === "number") {
                const cacheRecord: Record<string, any> = { ...record };
                this.customerCache.set(record.userId, cacheRecord);
                if (record.userId === params.userId) {
                  try {
                    localStorage.setItem(
                      "customerData",
                      JSON.stringify(cacheRecord)
                    );
                  } catch {
                    // ignore storage errors
                  }
                }
              }
            });

            return records;
          }
        } catch {
          // When the per-user endpoint fails, do not attempt the list endpoint.
          // This avoids hitting `/iehub-customer/api/v1/customers` without an id.
          return [];
        }
      }

      // When no userId is provided, do not call the legacy list endpoint at all.
      // This avoids hitting `/iehub-customer/api/v1/customers` without an id, which
      // is not supported in some environments and can return 500.
      return [];
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("onboarding_fetch_customers_error", error, {
          category: "api",
          severity: "warning",
          context: { userId: params.userId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Fetch vulnerability circumstances for a customer
   */
  static async getVulnerabilityCircumstances(
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<VulnerabilityCircumstancePayload | null> {
    try {
      const cached = this.vulnerabilityCache.get(customerId);
      if (cached) {
        return cached;
      }

      const response = await httpGet<VulnerabilityCircumstanceResponse>(
        onboardingEndpoints.getVulnerabilityCircumstances(customerId),
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Handle both response formats: { data: { data: [...] } } or { data: [...] }
      let payloadRaw = response.data?.data;
      if (!payloadRaw && Array.isArray(response.data)) {
        // If data is directly an array at response.data level
        payloadRaw = response.data;
      }

      const payload = Array.isArray(payloadRaw)
        ? (payloadRaw[0] as VulnerabilityCircumstancePayload | undefined)
        : (payloadRaw as VulnerabilityCircumstancePayload | undefined);

      if (payload && typeof payload === "object") {
        this.vulnerabilityCache.set(customerId, payload);
        return payload;
      }

      return null;
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("vulnerability_get_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      return null;
    }
  }

  /**
   * Customer Field Stage API
   * Remember and restore user's last field stage across sessions
   */
  static async getCustomerFieldStage(
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<CustomerFieldStageRecord | null> {
    try {
      const cached = this.fieldStageCache.get(customerId);
      if (cached) return cached;

      const response = await httpGet<
        | CustomerFieldStageResponse
        | CustomerFieldStageRecord[]
        | CustomerFieldStageRecord
        | null
      >(onboardingEndpoints.getCustomerFieldStage(customerId), {
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
      });

      // Handle both raw array/object and envelope { data: ... }
      const payload = (response as any)?.data;
      const raw = Array.isArray(payload) ? payload : (payload?.data ?? null);
      // Priority: if any stage >= 104 exists, pick the highest one (signals completed income onboarding)
      // Otherwise fall back to latest record in array
      let record: CustomerFieldStageRecord | undefined;
      if (Array.isArray(raw)) {
        // Find the record with the highest fieldStage >= 104 (completed income onboarding)
        const completedRecords = raw.filter((r: CustomerFieldStageRecord) => {
          const stage = parseInt(r.fieldStage, 10);
          return (
            !Number.isNaN(stage) &&
            stage >= INCOME_ONBOARDING_COMPLETED_THRESHOLD
          );
        });
        if (completedRecords.length > 0) {
          // Pick the one with the highest stage value
          record = completedRecords.reduce((max, curr) => {
            const maxStage = parseInt(max.fieldStage, 10);
            const currStage = parseInt(curr.fieldStage, 10);
            return currStage > maxStage ? curr : max;
          });
        } else {
          record = raw[raw.length - 1] as CustomerFieldStageRecord | undefined;
        }
      } else {
        record = raw as CustomerFieldStageRecord | undefined;
      }
      if (
        record &&
        typeof record === "object" &&
        typeof record.customerId === "number"
      ) {
        this.fieldStageCache.set(customerId, record);
        return record;
      }
      return null;
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("customer_field_stage_get_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      return null;
    }
  }

  static async upsertCustomerFieldStage(
    payload: CustomerFieldStageRecord,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<CustomerFieldStageRecord | null> {
    try {
      // Determine whether to POST or PUT by checking existing record first
      const existing = await this.getCustomerFieldStage(payload.customerId, {
        skipErrorCapture: true,
      });
      const doPost = !existing;
      const body = doPost
        ? {
            customerId: payload.customerId,
            fieldStage: payload.fieldStage,
          }
        : {
            // Use the id from the latest existing record to update
            id: (existing?.id ?? payload.id) as number,
            customerId: payload.customerId,
            fieldStage: payload.fieldStage,
            // ...(typeof payload.userId === 'number' ? { userId: payload.userId } : {}),
          };

      const response = doPost
        ? await httpPost<
            | CustomerFieldStageResponse
            | CustomerFieldStageRecord
            | CustomerFieldStageRecord[]
            | null
          >(onboardingEndpoints.createCustomerFieldStage, body, {
            timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          })
        : await httpPut<
            | CustomerFieldStageResponse
            | CustomerFieldStageRecord
            | CustomerFieldStageRecord[]
            | null
          >(onboardingEndpoints.updateCustomerFieldStage, body, {
            timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          });

      // Handle both raw and envelope responses
      const respPayload = (response as any)?.data;
      const upsertRaw = Array.isArray(respPayload)
        ? respPayload
        : (respPayload?.data ?? null);
      // Priority: if any stage >= 104 exists, pick the highest one; otherwise latest
      let record: CustomerFieldStageRecord | undefined;
      if (Array.isArray(upsertRaw)) {
        const completedRecords = upsertRaw.filter(
          (r: CustomerFieldStageRecord) => {
            const stage = parseInt(r.fieldStage, 10);
            return (
              !Number.isNaN(stage) &&
              stage >= INCOME_ONBOARDING_COMPLETED_THRESHOLD
            );
          }
        );
        if (completedRecords.length > 0) {
          // Pick the one with the highest stage value
          record = completedRecords.reduce((max, curr) => {
            const maxStage = parseInt(max.fieldStage, 10);
            const currStage = parseInt(curr.fieldStage, 10);
            return currStage > maxStage ? curr : max;
          });
        } else {
          record = upsertRaw[upsertRaw.length - 1] as
            | CustomerFieldStageRecord
            | undefined;
        }
      } else {
        record = upsertRaw as CustomerFieldStageRecord | undefined;
      }
      if (
        record &&
        typeof record === "object" &&
        typeof record.customerId === "number"
      ) {
        this.fieldStageCache.set(record.customerId, record);
        return record;
      }

      // If the response is empty but the request was successful,
      // optimistically update the cache with the payload we sent.
      if (response && (response.status === 200 || response.status === 201)) {
        this.fieldStageCache.set(payload.customerId, payload);
        // Return the payload we sent, as the backend didn't return the updated record
        return payload;
      }

      return null;
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("customer_field_stage_upsert_error", error, {
          category: "api",
          severity: "warning",
          context: {
            customerId: payload.customerId,
            stage: payload.fieldStage,
            userId: payload.userId,
          },
          skipSanitization: false,
          user: {},
        });
      }
      return null;
    }
  }

  /**
   * Create vulnerability circumstances for a customer (POST)
   */
  static async createVulnerabilityCircumstances(
    payload: VulnerabilityCircumstancePayload,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<VulnerabilityCircumstancePayload> {
    try {
      const response = await httpPost<VulnerabilityCircumstanceResponse>(
        onboardingEndpoints.createVulnerabilityCircumstances,
        payload,
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const createdRaw = response.data?.data;
      const record = Array.isArray(createdRaw)
        ? (createdRaw[0] as VulnerabilityCircumstancePayload | undefined)
        : (createdRaw as VulnerabilityCircumstancePayload | undefined);

      if (record && typeof record === "object") {
        this.vulnerabilityCache.set(record.customerId, record);
        return record;
      }

      throw new Error("Invalid create vulnerability response payload");
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("vulnerability_create_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Update vulnerability circumstances for a customer (PUT)
   */
  static async updateVulnerabilityCircumstances(
    recordId: number,
    payload: VulnerabilityCircumstancePayload,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<VulnerabilityCircumstancePayload> {
    try {
      const response = await httpPut<VulnerabilityCircumstanceResponse>(
        onboardingEndpoints.updateVulnerabilityCircumstances(recordId),
        payload,
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const updatedRaw = response.data?.data;
      const record = Array.isArray(updatedRaw)
        ? (updatedRaw[0] as VulnerabilityCircumstancePayload | undefined)
        : (updatedRaw as VulnerabilityCircumstancePayload | undefined);

      if (record && typeof record === "object") {
        this.vulnerabilityCache.set(record.customerId, record);
        return record;
      }

      throw new Error("Invalid update vulnerability response payload");
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("vulnerability_update_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Create new customer record
   */
  static async createCustomer(
    userId: number,
    _formData: Partial<CustomerUpdatePayload>,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<CustomerRecord>> {
    try {
      // Enrich minimally required fields per Swagger for create
      const nowIso = new Date().toISOString();
      // Minimal, strict payload per Swagger that backend should accept
      const basePayload: Partial<CustomerUpdatePayload> = {
        creationDate: nowIso,
        lastUpdateDate: nowIso,
        createdBy: userId,
        lastUpdatedBy: userId,
        // userId and customerId must be different; do NOT set customerId here.
        // Backend ie_free_post_customers model REJECTS userId field; omit it entirely.
      };

      const response = await httpPost(
        onboardingEndpoints.createCustomer,
        basePayload,
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.variant === "success" && response.data.myData) {
        const customerData = response.data.myData;
        const createdRecord: CustomerRecord = {
          id: customerData.id,
          customerId: customerData.customerId || customerData.id,
          userId: userId,
          creationDate: customerData.creationDate,
          lastUpdateDate: customerData.lastUpdateDate,
        };

        // IMPORTANT: Do NOT cache this under primary customer cache or localStorage
        // '/ie_free_post_customers' is a different entity than '/customers'
        return {
          success: true,
          data: createdRecord,
          message: response.data.message,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error(
        "Failed to create customer record - invalid response format"
      );
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("onboarding_create_customer_error", error, {
          category: "api",
          severity: "error",
          context: { userId },
          skipSanitization: false,
          user: {},
        });
      }

      throw error;
    }
  }

  /**
   * Transform form data from schema format to API format
   */
  private static transformToApiFormat(
    allFormData: Record<string, any>,
    customerRecord: CustomerRecord
  ): Record<string, any> {
    // Base payload - use existing customer record if available, otherwise defaults
    const apiPayload: Record<string, any> = customerRecord;

    // Transform each step's data using field mapping
    Object.entries(allFormData).forEach(([stepKey, stepData]) => {
      const mapping = SCHEMA_FIELD_MAPPING[stepKey];
      if (mapping && stepData) {
        Object.entries(mapping).forEach(([schemaField, apiField]) => {
          if (
            stepData[schemaField] !== undefined &&
            stepData[schemaField] !== null
          ) {
            // Handle special data transformations
            let value = stepData[schemaField];

            // Convert arrays to comma-separated strings for vulnerability circumstances
            if (Array.isArray(value)) {
              value = value.join(", ");
            }

            // Convert boolean to string for checkbox fields
            if (typeof value === "boolean") {
              value = value ? "Y" : "N";
            }

            // Format dates to YYYY-MM-DD format for API
            if (value instanceof Date && isValid(value)) {
              value = format(value, "yyyy-MM-dd");
            }

            // Normalise customer title casing for legacy API/creditor portal compatibility.
            // UI schema uses lowercase codes (e.g. "mr", "mrs"), but backend/creditor
            // expect initial caps ("Mr", "Mrs", etc.). Keep values flexible here in case
            // future schemas already send the correctly cased value.
            if (apiField === "customerTitle" && typeof value === "string") {
              const trimmed = value.trim();
              if (trimmed.length > 0) {
                value =
                  trimmed.charAt(0).toUpperCase() +
                  trimmed.slice(1).toLowerCase();
              }
            }

            (apiPayload as any)[apiField] = value;
          }
        });
      }
    });

    // Normalize consent field for customer payloads
    if (Object.prototype.hasOwnProperty.call(apiPayload, "field4TxtFld")) {
      const rawConsent = (apiPayload as Record<string, unknown>).field4TxtFld;
      if (rawConsent === "" || rawConsent === null) {
        (apiPayload as Record<string, unknown>).field4TxtFld = "";
      } else {
        const normalizedConsent = coerceConsentToHuman(
          rawConsent as string | undefined
        );
        if (normalizedConsent !== undefined) {
          (apiPayload as Record<string, unknown>).field4TxtFld =
            normalizedConsent;
        }
      }
    }

    // Special handling for company selection scenarios
    const step1Data = allFormData["onboarding-step-1"];
    if (step1Data && step1Data.notReferredByCompany === true) {
      // Scenario 4: "Not referred by a company" -> use fixed text
      (apiPayload as any).field5TxtFld =
        "I have not been referred by a company";
    } else if (step1Data && step1Data.referedFromCompany === "OTHER_COMPANY") {
      // Scenario 3: "Can't find my referring company" -> use otherCompanyName
      (apiPayload as any).field5TxtFld = step1Data.otherCompanyName;
    } else if (
      step1Data &&
      step1Data.productName &&
      step1Data.productName !== ""
    ) {
      // Scenario 2: Digital company with products -> use productName
      (apiPayload as any).field5TxtFld = step1Data.productName;
    } else if (
      step1Data &&
      step1Data.referedFromCompany &&
      step1Data.referedFromCompany !== "OTHER_COMPANY"
    ) {
      // Scenario 1: Digital company without products -> use referedFromCompany
      (apiPayload as any).field5TxtFld = step1Data.referedFromCompany;
    }

    // If step 0 (refer method) indicates no company flow, we usually clear any prior backend
    // company value to avoid stale data. However, do NOT clear when step 1 explicitly supplies
    // company details (including the "Not referred by a company" case), because that is a
    // deliberate user choice we want to persist.
    const step0Data = allFormData["onboarding-step-0"];
    const referMethod = step0Data?.referedFrom as string | undefined;
    const leadsToCompany =
      referMethod === "Referred by a company" ||
      referMethod === "Debt charity or advice service";
    const step1DataForClear = allFormData["onboarding-step-1"];
    const hasExplicitCompanySection = Boolean(
      step1DataForClear &&
        (step1DataForClear.notReferredByCompany === true ||
          (typeof step1DataForClear.referedFromCompany === "string" &&
            step1DataForClear.referedFromCompany.trim().length > 0) ||
          (typeof step1DataForClear.productName === "string" &&
            step1DataForClear.productName.trim().length > 0) ||
          (typeof step1DataForClear.otherCompanyName === "string" &&
            step1DataForClear.otherCompanyName.trim().length > 0))
    );
    if (referMethod && !leadsToCompany && !hasExplicitCompanySection) {
      // Explicitly clear company-derived backend field only when step 1 has no explicit state
      console.log(
        "🧹 Clearing field5TxtFld for non-company referral (no explicit step-1 data):",
        {
          referMethod,
          previousValue: (apiPayload as any).field5TxtFld,
        }
      );
      (apiPayload as any).field5TxtFld = "";
    }

    console.log("📤 Transformed API Payload:", {
      field5TxtFld: (apiPayload as any).field5TxtFld,
      heardAboutUs: (apiPayload as any).heardAboutUs,
      step0Data,
      step1Data: allFormData["onboarding-step-1"],
    });

    return apiPayload;
  }

  /**
   * Update customer data progressively during onboarding
   * Follows GET -> POST (if needed) -> PUT flow
   */
  static async updateCustomer(
    allFormData: Record<string, any>,
    userId: number,
    recordId: number,
    customerId: number,
    emailAddress: string,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ): Promise<ServiceResponse<Record<string, any>>> {
    try {
      // STEP 3: Update the customer record with form data
      // Resolve identifiers from provided values and local cache
      let resolvedRecordId = recordId;
      let resolvedCustomerId = customerId;

      if (resolvedRecordId === userId || !resolvedRecordId) {
        try {
          const stored = localStorage.getItem("customerData");
          if (stored) {
            const parsed = JSON.parse(stored) as Record<string, any>;
            const idCandidate = (parsed?.id ?? parsed?.customerId) as
              | number
              | undefined;
            if (
              typeof idCandidate === "number" &&
              idCandidate > 0 &&
              idCandidate !== userId
            ) {
              resolvedRecordId = idCandidate;
            }
          }
        } catch {
          // ignore parsing errors
        }
      }

      if (resolvedCustomerId === userId || !resolvedCustomerId) {
        try {
          const stored = localStorage.getItem("customerData");
          if (stored) {
            const parsed = JSON.parse(stored) as Record<string, any>;
            const custIdCandidate = (parsed?.customerId ?? parsed?.id) as
              | number
              | undefined;
            if (
              typeof custIdCandidate === "number" &&
              custIdCandidate > 0 &&
              custIdCandidate !== userId
            ) {
              resolvedCustomerId = custIdCandidate;
            }
          }
        } catch {
          // ignore parsing errors
        }
      }

      // Build a base record starting from the most complete existing data we have,
      // so that we do not unintentionally drop fields like textField2/textField3.
      // Priority: in-memory cache -> localStorage -> fresh GET -> minimal identifiers
      let baseRecord: Record<string, any> = {
        id: resolvedRecordId,
        customerId: resolvedCustomerId,
        userId: userId,
        emailAddress: emailAddress,
      };

      // Try in-memory cache first
      const cachedRecord = OnboardingService.customerCache.get(userId);
      console.log("📦 Cache lookup for userId:", userId, {
        hasCached: !!cachedRecord,
        cachedField5: cachedRecord?.field5TxtFld,
      });
      if (cachedRecord && typeof cachedRecord === "object") {
        baseRecord = { ...cachedRecord, ...baseRecord };
        console.log(
          "✅ Using cached record, field5TxtFld:",
          baseRecord.field5TxtFld
        );
      } else {
        // Try localStorage snapshot
        try {
          const stored = localStorage.getItem("customerData");
          if (stored) {
            const parsed = JSON.parse(stored) as Record<string, any>;
            if (parsed && typeof parsed === "object") {
              baseRecord = { ...parsed, ...baseRecord };
            }
          }
        } catch {
          // ignore storage errors
        }

        // As a last resort, do a lightweight GET to fetch existing record
        if (!cachedRecord) {
          try {
            const existingResponse =
              await OnboardingService.getCustomerByUserId(userId, {
                skipErrorCapture: true,
              });
            if (existingResponse.success && existingResponse.data) {
              baseRecord = { ...existingResponse.data, ...baseRecord };
            }
          } catch {
            // ignore fetch errors; proceed with minimal identifiers
          }
        }
      }

      // Ensure required identifiers exist with correct types for the transform step
      const baseCustomerRecord: CustomerRecord = {
        // Preserve all existing fields from the fetched/base record
        ...baseRecord,
        // Guarantee required identifiers are numbers
        id:
          typeof baseRecord.id === "number" ? baseRecord.id : resolvedRecordId,
        customerId:
          typeof baseRecord.customerId === "number"
            ? baseRecord.customerId
            : resolvedCustomerId,
        userId:
          typeof baseRecord.userId === "number" ? baseRecord.userId : userId,
        emailAddress:
          typeof baseRecord.emailAddress === "string"
            ? baseRecord.emailAddress
            : emailAddress,
      };

      const updatePayload = this.transformToApiFormat(
        allFormData,
        baseCustomerRecord
      );

      // Final guard: ensure userId and customerId are not identical in payload
      if ((updatePayload as any).customerId === userId) {
        delete (updatePayload as any).customerId;
      }

      const response = await httpPut<Record<string, any>>(
        onboardingEndpoints.updateCustomer,
        updatePayload,
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Update cache with latest data
      if (response.data) {
        const customerRecord = response.data;
        this.customerCache.set(userId, customerRecord);
        localStorage.setItem("customerData", JSON.stringify(customerRecord));
      }

      // Unlink or ensure placeholder share based on selection:
      // IMPORTANT: avoid calling sharing APIs on *every* customer update (e.g. name/address edits).
      // We only run this block when referral-related inputs actually change.
      try {
        const effectiveCustomerId =
          (typeof (response.data as any)?.customerId === "number"
            ? (response.data as any).customerId
            : baseCustomerRecord.customerId) ?? undefined;
        if (
          typeof effectiveCustomerId === "number" &&
          Number.isFinite(effectiveCustomerId) &&
          effectiveCustomerId > 0
        ) {
          const step1 = allFormData["onboarding-step-1"] as
            | Record<string, unknown>
            | undefined;

          const normalize = (v: unknown): string =>
            typeof v === "string" ? v.trim().toLowerCase() : "";

          const prevHeardAboutUs = normalize(
            (baseCustomerRecord as any)?.heardAboutUs
          );
          const nextHeardAboutUs = normalize(
            (updatePayload as any)?.heardAboutUs
          );
          const prevField5 = normalize(
            (baseCustomerRecord as any)?.field5TxtFld
          );
          const nextField5 = normalize((updatePayload as any)?.field5TxtFld);

          // Legacy encoding for "not referred": stored in field5TxtFld as a fixed string.
          const prevNotReferred =
            prevField5 === "i have not been referred by a company";
          const nextNotReferred = step1?.["notReferredByCompany"] === true;

          const referralInputsChanged =
            prevHeardAboutUs !== nextHeardAboutUs ||
            prevField5 !== nextField5 ||
            prevNotReferred !== nextNotReferred;

          if (referralInputsChanged) {
            // Only unlink when the user explicitly says they were NOT referred by a company.
            // For manual "OTHER_COMPANY" entries we now keep the referral linkage intact.
            if (nextNotReferred) {
              await this.unlinkProfileManagedDebtAndSharing(
                effectiveCustomerId,
                {
                  skipErrorCapture: true,
                }
              );
            } else {
              // Ensure placeholder share for "Referred by a company" journeys
              await this.ensureReferralPlaceholderShareForHeardAboutUs(
                effectiveCustomerId,
                allFormData,
                updatePayload
              );
            }
          }
        }
      } catch (ensureShareErr) {
        console.warn(
          "⚠️ Failed to ensure referral placeholder share from heardAboutUs:",
          ensureShareErr
        );
      }

      return {
        success: true,
        data: response.data,
        message: response.data.message,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("❌ Customer update flow failed:", error);

      if (!options.skipErrorCapture) {
        const contextUserId = (allFormData?.userId ??
          allFormData?.user?.userId) as string | number | undefined;
        femaCapture("onboarding_update_flow_error", error, {
          category: "api",
          severity: "warning",
          context: {
            userId: contextUserId,
            formDataKeys: Object.keys(allFormData),
            stepCount: Object.keys(allFormData).length,
          },
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Customer update flow failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Transform form data to Debt API format
   */
  private static transformToDebtFormat(
    allFormData: Record<string, any>,
    customerId: number,
    emailAddress?: string | null
  ): Record<string, any> | null {
    // Skip debt creation if user explicitly says they were not referred by a company.
    // NOTE: We still keep any existing debt record, but we will unlink it from the
    // profile flow elsewhere by clearing its `source` flag.
    const step1Data = allFormData["onboarding-step-1"];
    if (step1Data && step1Data.notReferredByCompany === true) {
      return null;
    }

    // Allow debt update when we have either an account type OR a reference intent.
    // For reference, we now treat the user's email address as a safe fallback when
    // the explicit Reference ID is left blank or "I don't have a reference" is selected.
    const rawAccountType =
      typeof step1Data?.accountType === "string"
        ? step1Data.accountType.trim()
        : "";
    const hasAccountType = rawAccountType.length > 0;

    const rawReferenceId =
      typeof step1Data?.referenceId === "string"
        ? step1Data.referenceId.trim()
        : "";
    const emailFallback =
      typeof emailAddress === "string" ? emailAddress.trim() : "";
    const effectiveAccountNumber =
      rawReferenceId.length > 0 ? rawReferenceId : emailFallback;
    const hasReferenceIntent = effectiveAccountNumber.length > 0;

    if (!hasAccountType && !hasReferenceIntent) {
      // No actionable debt data
      return null;
    }

    const debtPayload: Record<string, any> = {
      customerId: customerId,
      // Canonical marker for debts created from the referral/profile flow.
      // Legacy rows may still use "referred" – we treat both the same when reading,
      // but now always write "customer_profile" going forward.
      source: "customer_profile",
    };

    // Determine creditor value based on scenario
    let creditor = "";
    if (step1Data.referedFromCompany === "OTHER_COMPANY") {
      // Scenario 3: "Can't find my referring company" -> use otherCompanyName
      creditor = step1Data.otherCompanyName;
    } else if (step1Data.productName && step1Data.productName !== "") {
      // Scenario 2: Digital company with products -> use productName
      creditor = step1Data.productName;
    } else {
      // Scenario 1: Digital company without products -> use referedFromCompany
      creditor = step1Data.referedFromCompany;
    }

    // Add creditor to payload
    if (creditor) {
      debtPayload.creditor = creditor;
    }

    // Add accountType if present
    if (hasAccountType) {
      debtPayload.accTypeCode = rawAccountType;
    }

    // Always prefer an explicit referenceId; otherwise fall back to the user's email.
    // This ensures Debts and sharing always receive a stable identifier even when the
    // user ticks "I don't have any Reference ID" or leaves the field blank.
    if (effectiveAccountNumber.length > 0) {
      debtPayload.accountNumber = effectiveAccountNumber;
    }

    return debtPayload;
  }

  /**
   * Update customer and debt data together for step 1 (company referral)
   * This method handles both customer update and debt creation/update
   */
  static async updateCustomerAndDebt(
    allFormData: Record<string, any>,
    userId: number,
    recordId: number,
    customerId: number,
    emailAddress: string,
    options: {
      skipErrorCapture?: boolean;
      /** When true, skip the sharing upsert inside this method (caller handles sharing separately) */
      skipSharingUpsert?: boolean;
    } = {}
  ): Promise<ServiceResponse<{ customer: any; debt: any | null }>> {
    try {
      // 1. Update customer data (company name goes here)
      const customerResponse = await this.updateCustomer(
        allFormData,
        userId,
        recordId,
        customerId,
        emailAddress,
        options
      );

      // 2. Transform and update debt data if available
      const debtPayload = this.transformToDebtFormat(
        allFormData,
        customerId,
        emailAddress
      );
      let debtResponse = null;

      if (debtPayload) {
        // Try to update an existing *profile-managed* debt first. If none is found,
        // create a brand new debt record for the referral instead of mutating any
        // unrelated debts or previously unlinked referral debts.
        try {
          const existingRaw = await this.getDebts(customerId, options);
          const existingDebts: Array<Record<string, any>> = Array.isArray(
            existingRaw
          )
            ? existingRaw
            : existingRaw
              ? [existingRaw as Record<string, any>]
              : [];

          const profileManagedDebt =
            existingDebts.find(
              (d) =>
                d &&
                typeof d.source === "string" &&
                (d.source === "customer_profile" || d.source === "referred")
            ) || null;

          let shouldOverwriteProfileManaged = true;

          if (profileManagedDebt && typeof profileManagedDebt.id === "number") {
            const normalizeStr = (value: unknown): string =>
              typeof value === "string" ? value.trim().toLowerCase() : "";

            const isCreditorChanged =
              normalizeStr(profileManagedDebt.creditor) !==
              normalizeStr(debtPayload.creditor);
            const isDebtTypeChanged =
              normalizeStr(profileManagedDebt.accTypeCode) !==
              normalizeStr(debtPayload.accTypeCode);
            const isRefNumberChanged =
              normalizeStr(profileManagedDebt.accountNumber) !==
              normalizeStr(debtPayload.accountNumber);

            if (isCreditorChanged || isDebtTypeChanged || isRefNumberChanged) {
              // Check if the referred company's debt has been shared
              try {
                const [digitalResp, freepostResp] = await Promise.all([
                  DebtsService.getCustomerSharing(customerId, { skipErrorCapture: true }),
                  DebtsService.getFreePostSharing(customerId, { skipErrorCapture: true }),
                ]);

                const digitalList = Array.isArray(digitalResp?.data) ? digitalResp.data : [];
                const freepostList = Array.isArray(freepostResp?.data) ? freepostResp.data : [];

                const isSharedRow = (row: any): boolean => {
                  const submit = typeof row.submitDate === "string" ? row.submitDate.trim() : "";
                  const lastShared = typeof row.lastSharedDate === "string" ? row.lastSharedDate.trim() : "";
                  return Boolean(submit || lastShared);
                };

                const isProfileManagedRow = (row: any): boolean => {
                  const src = typeof row.source === "string" ? row.source.trim().toLowerCase() : "";
                  const isDeleted = row.isDeleted === "Y" || row.isDelete === true;
                  return !isDeleted && (src === "customer_profile" || src === "referred");
                };

                const hasSharedRecord = [...digitalList, ...freepostList].some(
                  (row) => isProfileManagedRow(row) && isSharedRow(row)
                );

                if (hasSharedRecord) {
                  shouldOverwriteProfileManaged = false;
                }
              } catch (sharingCheckErr) {
                console.warn("⚠️ Failed to check sharing status for profile-managed debt:", sharingCheckErr);
              }
            }
          }

          // Only ever update an existing *active* profile-managed debt if it hasn't been shared and changed.
          // If the previous referral debt has been unlinked (source === "unlink_customer_profile")
          // or there is no profile-managed debt yet (or we shouldn't overwrite it), we check for 
          // an exact match in all existing debts to avoid duplicates before falling back to creating a new one.
          if (profileManagedDebt && typeof profileManagedDebt.id === "number" && shouldOverwriteProfileManaged) {
            debtResponse = await this.updateDebts(
              { ...debtPayload, id: profileManagedDebt.id },
              options
            );
          } else {
            const normalizeStr = (value: unknown): string =>
              typeof value === "string" ? value.trim().toLowerCase() : "";

            const exactMatchDebt = existingDebts.find(
              (d) =>
                normalizeStr(d.creditor) ===
                  normalizeStr(debtPayload.creditor) &&
                normalizeStr(d.accountNumber ?? "") ===
                  normalizeStr(debtPayload.accountNumber ?? "")
            );

            if (exactMatchDebt && typeof exactMatchDebt.id === "number") {
              debtResponse = await this.updateDebts(
                { ...debtPayload, id: exactMatchDebt.id },
                options
              );
            } else {
              debtResponse = await this.createDebts(debtPayload, options);
            }

            // Unlink the old profile-managed debt if we decided not to overwrite it (e.g. because it was shared)
            // or if we adopted an exact match that isn't the profile-managed debt.
            if (profileManagedDebt && typeof profileManagedDebt.id === "number") {
               if (!shouldOverwriteProfileManaged || profileManagedDebt.id !== exactMatchDebt?.id) {
                 try {
                   await this.updateDebts(
                     { ...profileManagedDebt, source: "unlink_customer_profile" },
                     options
                   );
                 } catch (unlinkErr) {
                   console.warn("⚠️ Failed to unlink old profile-managed debt:", unlinkErr);
                 }
               }
            }
          }
        } catch (error) {
          console.warn(
            "⚠️ Debt update failed, attempting to create new debt:",
            error
          );
        }

        // After creating/updating the profile-managed debt, upsert sharing so referred
        // companies appear correctly on the share screen.
        // Skip sharing upsert when:
        // 1. company is manually entered (OTHER_COMPANY)
        // 2. caller explicitly requests to skip (skipSharingUpsert option)
        if (!options.skipSharingUpsert) {
          try {
            const step1 = allFormData["onboarding-step-1"] as
              | Record<string, unknown>
              | undefined;
            const isOtherCompany =
              typeof step1?.["referedFromCompany"] === "string" &&
              (step1?.["referedFromCompany"] as string) === "OTHER_COMPANY";
            if (!isOtherCompany) {
              const debtsResp = await DebtsService.getDebts(customerId, {
                skipErrorCapture: true,
              });
              const debts: CustomerDebtRecord[] = Array.isArray(debtsResp?.data)
                ? (debtsResp.data as CustomerDebtRecord[])
                : [];

              if (debts.length > 0) {
                const normalizeStr = (value: unknown): string =>
                  typeof value === "string" ? value.trim().toLowerCase() : "";

                const candidate: CustomerDebtRecord | undefined =
                  debts.find(
                    (d) =>
                      d &&
                      typeof d.source === "string" &&
                      (d.source === "customer_profile" ||
                        d.source === "referred")
                  ) ||
                  debts.find(
                    (d) =>
                      normalizeStr(d.creditor) ===
                        normalizeStr(debtPayload.creditor) &&
                      normalizeStr(d.accountNumber ?? "") ===
                        normalizeStr(debtPayload.accountNumber ?? "")
                  );

                if (candidate && typeof candidate.id === "number") {
                  const lookups = await SharingOrchestrator.fetchLookups();
                  const existingMaps = await SharingOrchestrator.fetchExisting(
                    Number(customerId)
                  );
                  await SharingOrchestrator.upsertForDebt(
                    Number(customerId),
                    candidate,
                    lookups,
                    existingMaps
                  );
                }
              }
            }
          } catch (shareError) {
            console.warn(
              "⚠️ Failed to upsert sharing for profile-managed debt (skipped or errored):",
              shareError
            );
          }
        }
      } else {
        // No new debt payload (e.g. user selected "Not referred by a company" or
        // there is no actionable accountType/reference change).
        // We still need to:
        // - unlink any existing profile-managed debt when the user says they were
        //   not referred by a company;
        // - and, in the legacy case, clear a stale referenceId only on that
        //   profile-managed debt.
        try {
          const step1Data = allFormData["onboarding-step-1"] as
            | Record<string, any>
            | undefined;
          const notReferredByCompany = step1Data?.notReferredByCompany === true;

          // 1) Unlink: if user explicitly says "Not referred by a company", clear
          //    the profile linkage but KEEP the debt record and related sharing rows.
          if (notReferredByCompany) {
            await this.unlinkProfileManagedDebtAndSharing(customerId, options);
            return {
              success: true,
              data: {
                customer: customerResponse.data,
                debt: null,
              },
              message:
                "Customer data updated and profile-managed debt unlinked",
              timestamp: new Date().toISOString(),
            };
          }

          const existingRaw = await this.getDebts(customerId, {
            skipErrorCapture: true,
          });
          const existingDebts: Array<Record<string, any>> = Array.isArray(
            existingRaw
          )
            ? existingRaw
            : existingRaw
              ? [existingRaw as Record<string, any>]
              : [];

          const profileManagedDebt =
            existingDebts.find(
              (d) =>
                d &&
                typeof d.source === "string" &&
                (d.source === "customer_profile" || d.source === "referred")
            ) || null;

          // 2) Legacy behaviour: if the user leaves reference blank without
          //    checking "I don't have any Reference ID", clear accountNumber
          //    on the profile-managed debt only (to avoid touching other debts).
          const noReferenceChecked = step1Data?.noReferenceId === true;
          const referenceIdRaw =
            typeof step1Data?.referenceId === "string"
              ? step1Data.referenceId.trim()
              : "";
          const shouldClearReferenceId =
            step1Data != null && !noReferenceChecked && referenceIdRaw === "";

          if (shouldClearReferenceId && profileManagedDebt?.id) {
            const existingAccountNumber = profileManagedDebt?.accountNumber as
              | string
              | undefined;
            if ((existingAccountNumber ?? "") !== "") {
              // As with unlinking, send a complete debt payload so the Debts API
              // receives all required fields, only changing accountNumber.
              const baseDebtPayload: Record<string, any> = {
                ...(profileManagedDebt as Record<string, any>),
              };
              baseDebtPayload.id = profileManagedDebt.id;
              baseDebtPayload.customerId = customerId;
              baseDebtPayload.accountNumber = "";

              await this.updateDebts(baseDebtPayload, options);
            }
          }
        } catch (clearErr) {
          console.warn(
            "⚠️ Failed to proactively clear backend referenceId (accountNumber):",
            clearErr
          );
        }
      }

      return {
        success: true,
        data: {
          customer: customerResponse.data,
          debt: debtResponse,
        },
        message: "Customer and debt data updated successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("❌ Customer and debt update failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("customer_debt_update_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Customer and debt update failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Unlink the profile-managed debt (source === "referred" / legacy "customer_profile")
   * and update any related sharing rows, without deleting debts or sharing records.
   */
  static async unlinkProfileManagedDebtAndSharing(
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<void> {
    try {
      const existingRaw = await this.getDebts(customerId, {
        skipErrorCapture: true,
      });
      const existingDebts: Array<Record<string, any>> = Array.isArray(
        existingRaw
      )
        ? existingRaw
        : existingRaw
          ? [existingRaw as Record<string, any>]
          : [];

      const profileManagedDebt =
        existingDebts.find(
          (d) =>
            d &&
            typeof d.source === "string" &&
            (d.source === "customer_profile" || d.source === "referred")
        ) || null;

      if (!profileManagedDebt || typeof profileManagedDebt.id !== "number") {
        return;
      }

      const debtId = profileManagedDebt.id as number;

      // First, change the source flag on the debt itself so it is no
      // longer treated as a profile-managed/referral debt. We keep an explicit
      // audit-style marker so downstream systems can see it was unlinked by
      // the customer profile flow.
      try {
        // Use the full existing debt record as the base payload to satisfy the
        // Debts API contract (it expects a complete upsert object, not a
        // partial patch). We only override the fields we intentionally change.
        const baseDebtPayload: Record<string, any> = {
          ...(profileManagedDebt as Record<string, any>),
        };
        baseDebtPayload.id = debtId;
        baseDebtPayload.customerId = customerId;
        baseDebtPayload.source = "unlink_customer_profile";

        await this.updateDebts(baseDebtPayload, options);
      } catch (unlinkErr) {
        console.warn(
          "⚠️ Failed to unlink profile-managed debt (source clear):",
          unlinkErr
        );
      }

      // Then, clear/update the source marker on any sharing rows that were
      // created via the referral/profile flow for this customer, while keeping
      // shareNow and other flags intact. This is based purely on the sharing
      // row's source (e.g. "referred"), not on a specific debt linkage.
      try {
        const [digitalResp, freepostResp] = await Promise.all([
          DebtsService.getCustomerSharing(customerId, {
            skipErrorCapture: true,
          }),
          DebtsService.getFreePostSharing(customerId, {
            skipErrorCapture: true,
          }),
        ]);

        const digitalList: CustomerSharingResponse[] = Array.isArray(
          digitalResp?.data
        )
          ? (digitalResp.data as CustomerSharingResponse[])
          : [];
        const freepostList: FreePostDataResponse[] = Array.isArray(
          freepostResp?.data
        )
          ? (freepostResp.data as FreePostDataResponse[])
          : [];

        const updates: Array<Promise<unknown>> = [];

        digitalList.forEach((row) => {
          const rowSource = row.source;
          const isReferred = rowSource === "referred";
          const isLegacyCustomerProfile = rowSource === "customer_profile";

          if (isReferred || isLegacyCustomerProfile) {
            const payload: CustomerSharingUpsertRequest = {
              id: row.id,
              customerId: row.customerId,
              ...(typeof row.debtId === "number" ? { debtId: row.debtId } : {}),
              lenderName: row.lenderName,
              accountNumber: row.accountNumber,
              schemaName: row.schemaName,
              subDomain: row.subDomain,
              shareNow: row.shareNow === "Y" ? "Y" : "N",
              isSharing: row.isSharing === "Y" ? "Y" : "N",
              // When unlinking a profile-managed referral, reclassify any
              // sharing rows that were tagged as coming from the customer
              // profile so downstream callers can distinguish them.
              source: "unlink_customer_profile",
            };
            updates.push(
              DebtsService.updateCustomerSharing(payload, {
                skipErrorCapture: true,
              })
            );
          }
        });

        freepostList.forEach((row) => {
          const rowSource = row.source;
          const isReferred = rowSource === "referred";
          const isLegacyCustomerProfile = rowSource === "customer_profile";

          if (isReferred || isLegacyCustomerProfile) {
            const payload: FreePostSharingPayload = {
              id: row.id,
              customerId: row.customerId,
              ...(typeof row.debtId === "number" ? { debtId: row.debtId } : {}),
              companyName: row.companyName,
              referenceNumber: row.referenceNumber,
              address: row.address,
              shareNow: row.shareNow === "Y" ? "Y" : "N",
              // When unlinking a profile-managed referral, reclassify any
              // sharing rows that were tagged as coming from the customer
              // profile so downstream callers can distinguish them.
              source: "unlink_customer_profile",
            };
            updates.push(
              DebtsService.updateFreePostSharing(payload, {
                skipErrorCapture: true,
              })
            );
          }
        });

        if (updates.length > 0) {
          await Promise.all(updates);
        }
      } catch (unlinkSharingErr) {
        console.warn(
          "⚠️ Failed to unlink referred sharing entries for profile-managed debt:",
          unlinkSharingErr
        );
      }
    } catch (clearErr) {
      console.warn(
        "⚠️ Failed to unlink profile-managed debt and sharing entries:",
        clearErr
      );
    }
  }

  /**
   * Unlink ONLY the profile-managed debt (source === "customer_profile" / legacy "referred")
   * by switching its source to "unlink_customer_profile".
   *
   * This intentionally does NOT touch sharing rows. Use
   * `unlinkProfileManagedDebtAndSharing` when you need to unlink both.
   */
  static async unlinkProfileManagedDebtOnly(
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<void> {
    try {
      const existingRaw = await this.getDebts(customerId, {
        skipErrorCapture: true,
      });
      const existingDebts: Array<Record<string, any>> = Array.isArray(
        existingRaw
      )
        ? existingRaw
        : existingRaw
          ? [existingRaw as Record<string, any>]
          : [];

      const profileManagedDebt =
        existingDebts.find(
          (d) =>
            d &&
            typeof d.source === "string" &&
            (d.source === "customer_profile" || d.source === "referred")
        ) || null;

      if (!profileManagedDebt || typeof profileManagedDebt.id !== "number") {
        return;
      }

      const debtId = profileManagedDebt.id as number;

      // Use full existing debt payload and only override the source marker.
      const baseDebtPayload: Record<string, any> = {
        ...(profileManagedDebt as Record<string, any>),
      };
      baseDebtPayload.id = debtId;
      baseDebtPayload.customerId = customerId;
      baseDebtPayload.source = "unlink_customer_profile";

      await this.updateDebts(baseDebtPayload, options);
    } catch (err) {
      console.warn("⚠️ Failed to unlink profile-managed debt only:", err);
    }
  }

  static async getCustomerVerificationDetails(userId: string | number) {
    try {
      const response = (await httpGet(
        onboardingEndpoints.getCustomerVerificationDetails(userId),
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      )) as Record<string, any>;

      return response.data;
    } catch (error) {
      console.error("❌ Customer verification details failed:", error);
      throw error;
    }
  }

  /**
   * Check whether customer verification is required for the given user
   */
  static async isCustomerVerificationRequired(
    userId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<VerificationRequiredResponse> {
    try {
      const response = (await httpGet(
        onboardingEndpoints.getCustomerVerificationDetails(userId),
        { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
      )) as
        | { data?: VerificationRequiredResponse }
        | VerificationRequiredResponse;

      const payload = (response as any)?.data ?? response;
      return payload as VerificationRequiredResponse;
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("onboarding_is_verification_required_error", error, {
          category: "api",
          severity: "warning",
          context: { userId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Creditor portal integration:
   * Returns Open Banking invitation records for a user (empty list if none / if call fails).
   */
  static async getOpenBankInvitationsByUserId(
    userId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<OpenBankInvitationRecord[]> {
    const isRecord = (v: unknown): v is Record<string, unknown> =>
      typeof v === "object" && v !== null;
    const isInvitation = (v: unknown): v is OpenBankInvitationRecord => {
      if (!isRecord(v)) return false;
      return typeof (v as { id?: unknown }).id === "number";
    };

    try {
      const response = await httpGet(
        onboardingEndpoints.getOpenBankInvitationsByUserId(userId),
        { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
      );

      const payload: unknown =
        (response as unknown as { data?: unknown })?.data ?? response;

      if (Array.isArray(payload)) {
        return payload.filter(isInvitation);
      }
      if (
        isRecord(payload) &&
        Array.isArray((payload as { data?: unknown }).data)
      ) {
        const inner = (payload as { data: unknown }).data;
        return Array.isArray(inner) ? inner.filter(isInvitation) : [];
      }
      return [];
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("openbank_invitations_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: { userId },
          skipSanitization: false,
          user: {},
        });
      }
      // Non-blocking: invitation check should never prevent normal onboarding
      return [];
    }
  }

  /**
   * Creditor portal integration:
   * Update an Open Banking invitation record (PUT /ie-openbank-invitation).
   *
   * Notes:
   * - Backend expects a full record payload (legacy parity + screenshot).
   * - We keep the original object shape and only overwrite fields provided in `updates`.
   */
  static async updateOpenBankInvitation(
    invitation: OpenBankInvitationRecord,
    updates: Partial<OpenBankInvitationRecord> = {},
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<OpenBankInvitationRecord> {
    try {
      const merged: OpenBankInvitationRecord = {
        ...invitation,
        ...updates,
      };

      const response = await httpPut<OpenBankInvitationRecord>(
        onboardingEndpoints.updateOpenBankInvitation,
        merged,
        { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
      );
      return response.data;
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("openbank_invitation_update_error", error, {
          category: "api",
          severity: "warning",
          context: {
            id: invitation?.id,
            userId: invitation?.userId,
            status: (updates as OpenBankInvitationRecord).status,
          },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Creditor portal integration:
   * Mark the current Open Banking invitation as rejected (customer or partner) if present.
   *
   * Notes:
   * - Intended for Open Banking-invited ("openBankingOnly") users when they cancel/logout.
   * - Non-destructive: will NOT overwrite "Submitted" or an existing "Rejected" status.
   * - Uses updateOpenBankInvitation to send a full-record payload (legacy parity).
   */
  static async rejectOpenBankInvitationIfPresent(
    user: { userId?: number; isPartner?: string } | null | undefined,
    _options: { skipErrorCapture?: boolean } = {}
  ): Promise<void> {
    const userId = user?.userId;
    if (typeof userId !== "number" || userId <= 0) return;

    const invites = await OnboardingService.getOpenBankInvitationsByUserId(userId, {
      skipErrorCapture: true,
    });
    const first = Array.isArray(invites) ? invites[0] : undefined;
    if (!first || typeof first.id !== "number") return;

    const isPartnerUser = String(user?.isPartner ?? "N").trim().toUpperCase() === "Y";
    const currentStatusRaw = isPartnerUser ? first.partnersStatus : first.status;
    const currentStatus =
      typeof currentStatusRaw === "string" ? currentStatusRaw.trim() : "";

    // Avoid overwriting terminal statuses
    if (
      currentStatus === "Submitted" ||
      currentStatus === "Partner Rejected" ||
      currentStatus === "Customer Rejected"
    ) {
      return;
    }

    const now = Date.now();
    const updates: Partial<OpenBankInvitationRecord> = isPartnerUser
      ? { partnersStatus: "Partner Rejected", partnersStatusDate: now }
      : { status: "Customer Rejected", customerStatusDate: now };

    await OnboardingService.updateOpenBankInvitation(first, updates, {
      skipErrorCapture: true,
    });
  }

  /**
   * Update customer data progressively during onboarding
   * Follows GET -> POST (if needed) -> PUT flow
   */
  static async updateCustomerVerification(
    allFormData: Record<string, any>,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ): Promise<Record<string, any>> {
    try {
      const response = await httpPost<Record<string, any>>(
        onboardingEndpoints.updateCustomerVerification,
        {
          postcode: allFormData.postcode,
          verifiedCustomer: "Y",
        },
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      return response;
    } catch (error) {
      console.error("❌ Customer update flow failed:", error);

      if (!options.skipErrorCapture) {
        const contextUserId = (allFormData?.userId ??
          allFormData?.user?.userId) as string | number | undefined;
        femaCapture("onboarding_update_flow_error", error, {
          category: "api",
          severity: "warning",
          context: {
            userId: contextUserId,
            formDataKeys: Object.keys(allFormData),
            stepCount: Object.keys(allFormData).length,
          },
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Customer update flow failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // debts apis
  // get
  static async getDebts(
    customerId: number,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ) {
    try {
      const response = await httpGet(onboardingEndpoints.getDebts(customerId), {
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      console.error("❌ debts get failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("debts_get_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message: error instanceof Error ? error.message : "debts get failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // update
  static async updateDebts(
    debts: Record<string, any>,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ) {
    try {
      const response = await httpPut(onboardingEndpoints.updateDebts, debts, {
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      console.error("❌ debts update failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("debts_update_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message: error instanceof Error ? error.message : "debts update failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // create
  static async createDebts(
    debts: Record<string, any>,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ) {
    try {
      const response = await httpPost(onboardingEndpoints.createDebts, debts, {
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
      });
      return response.data;
    } catch (error) {
      console.error("❌ debts create failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("debts_create_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message: error instanceof Error ? error.message : "debts create failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // delete
  static async deleteDebts(
    customerId: number,
    debtId: number,
    options: {
      skipErrorCapture?: boolean;
    } = {}
  ) {
    try {
      const response = await httpDelete(
        onboardingEndpoints.deleteDebts(customerId, debtId),
        {
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
        }
      );
      return response.data;
    } catch (error) {
      console.error("❌ debts create failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("debts_create_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message: error instanceof Error ? error.message : "debts create failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // vulnerabilities
  static async getVulnerability(
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ) {
    try {
      const response = await httpGet(
        onboardingEndpoints.getVulnerability(customerId),
        { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
      );
      return response.data;
    } catch (error) {
      console.error("❌ get vulnerabilities failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("get_vulnerabilities_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message:
          error instanceof Error ? error.message : "get vulnerabilities failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // create
  static async createVulnerability(
    payload: Record<string, any>,
    options: { skipErrorCapture?: boolean } = {}
  ) {
    try {
      // If payload has onboarding-step-9, transform it; otherwise assume already transformed
      let transformedPayload = payload;
      if (payload["onboarding-step-9"]) {
        const step9Data = payload["onboarding-step-9"] || {};
        const selectedCircumstances =
          step9Data.vulnerabilityCircumstancesDetails || [];

        transformedPayload = {
          customerId: payload.customerId,
          userId: payload.userId || payload["onboarding-step-0"]?.userId,
          textColumn1: selectedCircumstances.includes("textColumn1")
            ? "Y"
            : "N",
          coercion: selectedCircumstances.includes("coercion") ? "Y" : "N",
          cognitiveDisorder: selectedCircumstances.includes("cognitiveDisorder")
            ? "Y"
            : "N",
          financialCapability: selectedCircumstances.includes(
            "financialCapability"
          )
            ? "Y"
            : "N",
          financialDifficulty: selectedCircumstances.includes(
            "financialDifficulty"
          )
            ? "Y"
            : "N",
          addiction: selectedCircumstances.includes("addiction") ? "Y" : "N",
          lifeCycleEvent: selectedCircumstances.includes("lifeCycleEvent")
            ? "Y"
            : "N",
          mentalHealth: selectedCircumstances.includes("mentalHealth")
            ? "Y"
            : "N",
          physicalHealth: selectedCircumstances.includes("physicalHealth")
            ? "Y"
            : "N",
          textColumn2: selectedCircumstances.includes("textColumn2")
            ? "Y"
            : "N",
          textColumn3: selectedCircumstances.includes("textColumn3")
            ? "Y"
            : "N",
        };
      }

      const response = await httpPost(
        onboardingEndpoints.createVulnerability,
        transformedPayload,
        { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
      );
      return response.data;
    } catch (error) {
      console.error("❌ create vulnerabilities failed:", error);

      if (!options.skipErrorCapture) {
        femaCapture("create_vulnerabilities_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }

      throw {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "create vulnerabilities failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // update
  static async updateVulnerability(
    payload: Record<string, any>,
    customerId: number,
    options: { skipErrorCapture?: boolean } = {}
  ) {
    try {
      // Transform array format to individual Y/N fields
      const step9Data = payload["onboarding-step-9"] || {};
      const selectedCircumstances =
        step9Data.vulnerabilityCircumstancesDetails || [];

      const transformedPayload = {
        customerId,
        userId: payload.userId || payload["onboarding-step-0"]?.userId,
        textColumn1: selectedCircumstances.includes("textColumn1") ? "Y" : "N",
        coercion: selectedCircumstances.includes("coercion") ? "Y" : "N",
        cognitiveDisorder: selectedCircumstances.includes("cognitiveDisorder")
          ? "Y"
          : "N",
        financialCapability: selectedCircumstances.includes(
          "financialCapability"
        )
          ? "Y"
          : "N",
        financialDifficulty: selectedCircumstances.includes(
          "financialDifficulty"
        )
          ? "Y"
          : "N",
        addiction: selectedCircumstances.includes("addiction") ? "Y" : "N",
        lifeCycleEvent: selectedCircumstances.includes("lifeCycleEvent")
          ? "Y"
          : "N",
        mentalHealth: selectedCircumstances.includes("mentalHealth")
          ? "Y"
          : "N",
        physicalHealth: selectedCircumstances.includes("physicalHealth")
          ? "Y"
          : "N",
        textColumn2: selectedCircumstances.includes("textColumn2") ? "Y" : "N",
        textColumn3: selectedCircumstances.includes("textColumn3") ? "Y" : "N",
      };

      // check if vulnerability is exist for this customer id or not
      const isExist = await this.getVulnerability(customerId, {
        skipErrorCapture: true,
      });
      if (Array.isArray(isExist) && isExist.length > 0) {
        const response = await httpPut(
          onboardingEndpoints.updateVulnerability,
          { ...transformedPayload, id: isExist[0].id },
          { timeout: ONBOARDING_REQUEST_TIMEOUT_MS }
        );
        return response.data;
      } else {
        return this.createVulnerability(transformedPayload, options);
      }
    } catch (error) {
      console.error("❌ update vulnerabilities failed:", error);
      if (!options.skipErrorCapture) {
        femaCapture("update_vulnerabilities_error", error, {
          category: "api",
          severity: "warning",
          skipSanitization: false,
          user: {},
        });
      }
      throw {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "update vulnerabilities failed",
        error: error,
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Get field mapping for debugging
   */
  static getFieldMapping(): Record<string, Record<string, string>> {
    return SCHEMA_FIELD_MAPPING;
  }

  /**
   * Validate that all required fields are present
   */
  static validateFormData(allFormData: Record<string, any>): {
    isValid: boolean;
    missingFields: string[];
  } {
    const missingFields: string[] = [];

    // Check for critical fields (adjust based on business requirements)
    const criticalSteps = [
      "onboarding-step-3",
      "onboarding-step-4",
      "onboarding-step-5",
    ];

    criticalSteps.forEach((stepKey) => {
      if (!allFormData[stepKey]) {
        missingFields.push(stepKey);
      }
    });

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  }

  /**
   * Clear customer cache (useful for testing or when switching users)
   */
  static clearCustomerCache(userId?: number): void {
    if (userId) {
      this.customerCache.delete(userId);
    } else {
      this.customerCache.clear();
    }
  }

  /**
   * Get cached customer record (for debugging)
   */
  static getCachedCustomer(userId: number): Record<string, any> | undefined {
    return this.customerCache.get(userId);
  }

  /**
   * Transform customer data from API format to schema format for prefill
   * This is the reverse of transformToApiFormat - converts API fields back to schema fields
   */
  static transformCustomerDataToPrefill(
    customerData: Record<string, any>
  ): Record<string, Record<string, any>> {
    const prefillData: Record<string, Record<string, any>> = {};

    // Create reverse mapping from API fields to schema fields
    Object.entries(SCHEMA_FIELD_MAPPING).forEach(([stepKey, fieldMapping]) => {
      const stepData: Record<string, any> = {};

      Object.entries(fieldMapping).forEach(([schemaField, apiField]) => {
        const rawValue = customerData[apiField];

        if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
          const normalizedValue =
            apiField === "field4TxtFld"
              ? (coerceConsentToLegacy(rawValue as string | undefined) ??
                rawValue)
              : rawValue;

          let transformedValue = normalizedValue;

          // Handle special data transformations (reverse of API transformations)

          // Convert Y/N strings back to boolean for checkbox fields ONLY (not radio groups)
          if (
            typeof normalizedValue === "string" &&
            (normalizedValue === "Y" || normalizedValue === "N")
          ) {
            // Don't convert healthDataSharingConsent - it's a radio group expecting 'Y'/'N' strings
            if (schemaField !== "healthDataSharingConsent") {
              transformedValue = normalizedValue === "Y";
            }
          }

          // Convert comma-separated strings back to arrays for vulnerability circumstances
          // if (typeof apiValue === 'string' && apiValue.includes(',') &&
          //     (schemaField === 'vulnerabilityCircumstances' || apiField === 'field5Txtfld')) {
          //   transformedValue = apiValue.split(',').map(item => item.trim()).filter(item => item);
          // }

          // Handle date strings - convert to Date objects for date fields
          if (
            schemaField === "dateOfBirth" &&
            typeof normalizedValue === "string"
          ) {
            try {
              // Try to parse the date string
              const parsedDate = new Date(normalizedValue);
              if (isValid(parsedDate)) {
                transformedValue = parsedDate;
              }
            } catch (error) {
              console.warn(
                `Failed to parse date for ${schemaField}:`,
                normalizedValue
              );
              // Keep original value if parsing fails
            }
          }

          // Handle special case for "Not Referred By Company" checkbox
          if (
            schemaField === "notReferredByCompany" &&
            normalizedValue === "Y"
          ) {
            transformedValue = true;
          }

          stepData[schemaField] = transformedValue;
        }
      });

      // Only add step data if it has any values
      if (Object.keys(stepData).length > 0) {
        prefillData[stepKey] = stepData;
      }
    });

    // Special handling for step 1 (company selection) - field5TxtFld
    // This field determines the company selection and takes priority over debt data
    const field5TxtFldValue = customerData.field5TxtFld;
    if (field5TxtFldValue && typeof field5TxtFldValue === "string") {
      if (field5TxtFldValue === "I have not been referred by a company") {
        // Scenario 4: User checked "Not referred by a company"
        prefillData["onboarding-step-1"] = {
          ...prefillData["onboarding-step-1"],
          notReferredByCompany: true,
        };
      } else {
        // For scenarios 1, 2, 3: Company name from field5TxtFld will be resolved by
        // loadCustomerPrefillData logic which checks if it's a parent/child company or OTHER_COMPANY
        // Store it temporarily as referedFromCompany, will be refined later
        prefillData["onboarding-step-1"] = {
          ...prefillData["onboarding-step-1"],
          _tempCompanyName: field5TxtFldValue,
        };
      }
    }

    return prefillData;
  }

  /**
   * Transform debt data from API format to schema format for prefill
   * This is the reverse of transformToDebtFormat - converts API debt fields back to schema fields
   */
  private static transformDebtDataToPrefill(
    debtData: Record<string, any>
  ): Record<string, Record<string, any>> {
    const prefillData: Record<string, Record<string, any>> = {};

    // Step 1: Handle debt data
    const step1Data: Record<string, any> = {};

    // Map creditor - will be refined by loadCustomerPrefillData logic
    if (debtData.creditor) {
      step1Data.referedFromCompany = debtData.creditor;
    }

    // Map accountType
    if (debtData.accTypeCode) {
      step1Data.accountType = debtData.accTypeCode;
    }

    // Map referenceId
    if (debtData.accountNumber) {
      step1Data.referenceId = debtData.accountNumber;
    }

    if (Object.keys(step1Data).length > 0) {
      prefillData["onboarding-step-1"] = step1Data;
    }

    return prefillData;
  }

  /**
   * Transform vulnerability data from API format to schema format for prefill
   * Converts individual Y/N flags back to array format and derives hasVulnerabilityCircumstances
   */
  private static transformVulnerabilityDataToPrefill(
    vulnerabilityData: Record<string, any>
  ): Record<string, Record<string, any>> {
    const prefillData: Record<string, Record<string, any>> = {};

    // Mapping of API field names to checkbox values for step 9
    const circumstanceMapping: Record<string, string> = {
      textColumn1: "textColumn1",
      coercion: "coercion",
      cognitiveDisorder: "cognitiveDisorder",
      financialCapability: "financialCapability",
      financialDifficulty: "financialDifficulty",
      addiction: "addiction",
      lifeCycleEvent: "lifeCycleEvent",
      mentalHealth: "mentalHealth",
      physicalHealth: "physicalHealth",
      textColumn2: "textColumn2",
      textColumn3: "textColumn3",
    };

    // Step 7: Derive hasVulnerabilityCircumstances from saved flags
    // If there is a vulnerability record, remember explicit 'yes' or 'no'
    const hasAnyFlagValue = Object.keys(circumstanceMapping).some(
      (apiField) =>
        vulnerabilityData[apiField] === "Y" ||
        vulnerabilityData[apiField] === "N"
    );
    const hasAnyVulnerability = Object.keys(circumstanceMapping).some(
      (apiField) => vulnerabilityData[apiField] === "Y"
    );

    if (hasAnyFlagValue) {
      prefillData["onboarding-step-7"] = {
        hasVulnerabilityCircumstances: hasAnyVulnerability ? "yes" : "no",
      };
    }

    // Step 8 & 10: Map direct fields using VULNERABILITY_FIELD_MAPPING
    Object.entries(VULNERABILITY_FIELD_MAPPING).forEach(
      ([stepKey, fieldMapping]) => {
        const stepData: Record<string, any> = {};

        Object.entries(fieldMapping).forEach(([schemaField, apiField]) => {
          const apiValue = vulnerabilityData[apiField];

          if (apiValue !== undefined && apiValue !== null && apiValue !== "") {
            stepData[schemaField] = apiValue;
          }
        });

        // Only add step data if it has any values
        if (Object.keys(stepData).length > 0) {
          prefillData[stepKey] = stepData;
        }
      }
    );

    // Step 9: Convert Y/N flags to array of selected circumstances
    const selectedCircumstances: string[] = [];

    Object.entries(circumstanceMapping).forEach(([apiField, checkboxValue]) => {
      if (vulnerabilityData[apiField] === "Y") {
        selectedCircumstances.push(checkboxValue);
      }
    });

    if (selectedCircumstances.length > 0) {
      prefillData["onboarding-step-9"] = {
        vulnerabilityCircumstancesDetails: selectedCircumstances,
      };
    }

    return prefillData;
  }

  /**
   * Load and transform customer data for prefill
   * Returns prefill data ready to be applied to form state
   */
  static async loadCustomerPrefillData(
    userId: number,
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<Record<string, Record<string, any>> | null>> {
    try {
      // Get customer data
      const customerResponse = await this.getCustomerByUserId(userId, options);

      if (!customerResponse.success || !customerResponse.data) {
        return {
          success: false,
          data: null,
          message: "No customer data found for prefill",
          timestamp: new Date().toISOString(),
        };
      }

      // Transform customer data to prefill format
      const prefillData = this.transformCustomerDataToPrefill(
        customerResponse.data
      );

      // Resolve company selection from field5TxtFld if present
      if (prefillData["onboarding-step-1"]?._tempCompanyName) {
        const companyName = prefillData["onboarding-step-1"]._tempCompanyName;
        delete prefillData["onboarding-step-1"]._tempCompanyName;

        try {
          const { LenderService } = await import("./lenderService");
          const lendersResponse = await LenderService.fetchLenders({
            skipErrorCapture: true,
          });

          if (lendersResponse.success && lendersResponse.data) {
            // Check if company name is a parent company
            const parentLender = lendersResponse.data.find(
              (lender) => lender.parentCompany === companyName
            );

            if (parentLender) {
              // Scenario 1: Company name is a parent company (digital company without products)
              prefillData["onboarding-step-1"].referedFromCompany = companyName;
            } else {
              // Check if company name is a child company (product)
              const childLender = lendersResponse.data.find((lender) =>
                lender.childCompany.includes(companyName)
              );

              if (childLender) {
                // Scenario 2: Company name is a product name (digital company with products)
                prefillData["onboarding-step-1"].referedFromCompany =
                  childLender.parentCompany;
                prefillData["onboarding-step-1"].productName = companyName;
              } else {
                // Scenario 3: Company name not found in lenders (non-digital company)
                prefillData["onboarding-step-1"].referedFromCompany =
                  "OTHER_COMPANY";
                prefillData["onboarding-step-1"].otherCompanyName = companyName;
              }
            }
          }
        } catch (lenderError) {
          // Non-blocking: if lender fetch fails, set as-is
          console.warn(
            "Failed to fetch lenders for field5TxtFld prefill:",
            lenderError
          );
          prefillData["onboarding-step-1"].referedFromCompany = companyName;
        }
      }

      // Get customerId for fetching debt data
      const customerId =
        customerResponse.data.customerId || customerResponse.data.id;

      // Fetch and merge debt data if customerId is available
      if (customerId && typeof customerId === "number") {
        try {
          const debtData = await this.getDebts(customerId, {
            skipErrorCapture: true,
          });

          // debtData could be an array or object, handle both cases.
          // Only use debts explicitly linked to the referral/profile flow.
          // Accepted sources: 'customer_profile' (current flow), 'referred' (legacy).
          // Do NOT fall back to arbitrary debts to avoid contamination from unrelated debts.
          if (debtData) {
            const debtArray: Array<Record<string, any>> = Array.isArray(
              debtData
            )
              ? debtData
              : [debtData as Record<string, any>];

            const referralSources = new Set(["customer_profile", "referred"]);
            const debtRecord: Record<string, any> | null =
              debtArray.find(
                (d) =>
                  d &&
                  typeof d.source === "string" &&
                  referralSources.has(d.source)
              ) || null;

            if (debtRecord && typeof debtRecord === "object") {
              // Transform debt data to prefill format
              const debtPrefillData =
                this.transformDebtDataToPrefill(debtRecord);

              // Handle reference ID checkbox logic for step 1
              if (debtPrefillData["onboarding-step-1"]) {
                const hasAccountNumber =
                  debtRecord.accountNumber &&
                  debtRecord.accountNumber !== "" &&
                  debtRecord.accountNumber !== null &&
                  debtRecord.accountNumber !== undefined;

                if (!hasAccountNumber) {
                  debtPrefillData["onboarding-step-1"].noReferenceId = true;
                }
              }

              // Merge debt prefill data with customer prefill data
              // IMPORTANT: Customer data (field5TxtFld) takes priority over debt data
              // Only merge accountType and referenceId from debt data, not company selection
              Object.entries(debtPrefillData).forEach(([stepKey, stepData]) => {
                if (stepKey === "onboarding-step-1") {
                  // For step 1, only add accountType and referenceId from debt
                  // Company selection (referedFromCompany, productName, otherCompanyName, notReferredByCompany)
                  // should come from customer API field5TxtFld
                  const debtOnlyFields: Record<string, any> = {};

                  if (stepData.accountType) {
                    debtOnlyFields.accountType = stepData.accountType;
                  }

                  if (stepData.referenceId) {
                    debtOnlyFields.referenceId = stepData.referenceId;
                  }

                  if (stepData.noReferenceId !== undefined) {
                    debtOnlyFields.noReferenceId = stepData.noReferenceId;
                  }

                  // Merge debt-only fields with existing customer data
                  if (prefillData[stepKey]) {
                    prefillData[stepKey] = {
                      ...prefillData[stepKey],
                      ...debtOnlyFields,
                    };
                  } else if (Object.keys(debtOnlyFields).length > 0) {
                    prefillData[stepKey] = debtOnlyFields;
                  }
                } else {
                  // For other steps, merge normally
                  if (prefillData[stepKey]) {
                    prefillData[stepKey] = {
                      ...prefillData[stepKey],
                      ...stepData,
                    };
                  } else {
                    prefillData[stepKey] = stepData;
                  }
                }
              });
            }
          }
        } catch (debtError) {
          // Non-blocking: if debt fetch fails, continue with customer data only
          console.warn("Failed to fetch debt data for prefill:", debtError);
        }

        // Fetch and merge vulnerability data if customerId is available
        try {
          const vulnerabilityData = await this.getVulnerability(customerId, {
            skipErrorCapture: true,
          });

          // vulnerabilityData is typically an array
          if (
            vulnerabilityData &&
            Array.isArray(vulnerabilityData) &&
            vulnerabilityData.length > 0
          ) {
            const vulnerabilityRecord = vulnerabilityData[0];

            if (
              vulnerabilityRecord &&
              typeof vulnerabilityRecord === "object"
            ) {
              // Transform vulnerability data to prefill format
              const vulnerabilityPrefillData =
                this.transformVulnerabilityDataToPrefill(vulnerabilityRecord);

              // Merge vulnerability prefill data with existing prefill data
              Object.entries(vulnerabilityPrefillData).forEach(
                ([stepKey, stepData]) => {
                  if (prefillData[stepKey]) {
                    // Merge with existing step data
                    prefillData[stepKey] = {
                      ...prefillData[stepKey],
                      ...stepData,
                    };
                  } else {
                    // Add new step data
                    prefillData[stepKey] = stepData;
                  }
                }
              );
            }
          }
        } catch (vulnerabilityError) {
          // Non-blocking: if vulnerability fetch fails, continue with customer data only
          console.warn(
            "Failed to fetch vulnerability data for prefill:",
            vulnerabilityError
          );
        }
      }

      return {
        success: true,
        data: prefillData,
        message: "Customer prefill data loaded successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("onboarding_prefill_load_error", error, {
          category: "api",
          severity: "warning",
          context: { userId },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: null,
        message: "Failed to load customer prefill data",
        timestamp: new Date().toISOString(),
      };
    }
  }
}

export default OnboardingService;
